/**
 * NOLAS 2026, © Mika (Mikaël FOURNY).
 * src/i18n/fr.acces.ts
 *
 * Chantier J, accessibilité (RGAA / WCAG AA).
 *
 * Clés des libellés accessibles : titres de page distincts, étiquettes de
// champs, messages d'alerte annoncés aux lecteurs d'écran.
 *
 * Pourquoi un sous-catalogue plutôt que `fr.ts` :
 *   `fr.ts` est LE point de contact de tous les chantiers. Au lot précédent il
 *   avait été GELÉ, et chaque agent avait dû SIGNALER ses clés au lieu de les
 *   écrire, 47 clés à reporter à la main après la fusion, et un chantier dont
 *   les tests contredisaient son propre rapport. Cette fois chaque chantier a
 *   son fichier : il y écrit librement, et `fr.ts` ne bouge plus.
 *
 * ⚠ Jamais « salon » ni « atelier » en dur : jetons {eventType*} /
 *   {lexWorkshop*}, sans quoi le garde-fou d'identité passe au rouge.
 *
 * @author Mika
 * @since  2026-09-09
 */
export const messagesAccesFr: Record<string, string> = {
  // ── Titres de document, un par écran (WCAG 2.4.2 / RGAA 8.5) ──────────
  // Assemblés par DocumentBranding avec le nom de l’événement, qui vient de
  // la configuration, jamais d’un libellé écrit ici.
  'acces.titre.gabarit': '{ecran} ({evenement})',
  'acces.titre.accueil': 'Accueil',
  'acces.titre.splash': 'Ouverture',
  'acces.titre.onboarding': 'Découverte de l’application',
  'acces.titre.role': 'Choix du profil',
  'acces.titre.inscriptionRole': 'Inscription (formulaire)',
  'acces.titre.inscription': 'Inscription',
  'acces.titre.connexion': 'Connexion',
  'acces.titre.accepterInvitation': 'Acceptation d’une invitation',
  'acces.titre.installIos': 'Installer l’application sur iPhone ou iPad',
  'acces.titre.programme': 'Programme',
  'acces.titre.salon': '{eventTypeDefCap}',
  'acces.titre.atelierDetail': 'Fiche {lexWorkshopOfDef}',
  'acces.titre.exposantDetail': 'Fiche {lexExhibitorOfDef}',
  'acces.titre.exposants': '{lexExhibitorPluralCap}',
  'acces.titre.postes': 'Postes à pourvoir',
  'acces.titre.posteDetail': 'Fiche du poste',
  'acces.titre.carte': 'Plan des lieux',
  'acces.titre.kiosque': 'Borne d’accueil',
  'acces.titre.partenaires': 'Partenaires',
  'acces.titre.partenaireDetail': 'Fiche partenaire',
  'acces.titre.organisateur': 'Organisateur',
  'acces.titre.medias': 'Médias',
  'acces.titre.mediaDetail': 'Fiche média',
  'acces.titre.videos': 'Vidéos',
  'acces.titre.webradio': 'Webradio',
  'acces.titre.webtv': 'WebTV',
  'acces.titre.retrospective': 'Rétrospective',
  'acces.titre.presse': 'Espace presse',
  'acces.titre.aPropos': 'À propos',
  'acces.titre.contact': 'Contact',
  'acces.titre.accessibilite': 'Déclaration d’accessibilité',
  'acces.titre.mentionsLegales': 'Mentions légales',
  'acces.titre.confidentialite': 'Confidentialité des données',
  'acces.titre.plus': 'Plus',
  'acces.titre.pageCms': 'Page d’information',
  'acces.titre.monEspace': 'Mon espace',
  'acces.titre.monCompte': 'Mon compte',
  'acces.titre.monBadge': 'Mon badge',
  'acces.titre.monBadgeImpression': 'Mon badge (impression)',
  'acces.titre.maCarte': 'Ma carte',
  'acces.titre.monProgramme': 'Mon programme',
  'acces.titre.monParcours': 'Mon parcours',
  'acces.titre.monProfil': 'Mon profil',
  'acces.titre.mesFavoris': 'Mes favoris',
  'acces.titre.mesContacts': 'Mes contacts',
  'acces.titre.monGroupe': 'Mon groupe',
  'acces.titre.monGroupeListe': 'Mon groupe (liste nominative)',
  'acces.titre.sondages': 'Sondages',
  'acces.titre.questionnaireIa': 'Mon questionnaire d’orientation',
  'acces.titre.notificationsPrefs': 'Préférences de notifications',
  'acces.titre.controle': 'Contrôle des accès',
  'acces.titre.scanner': 'Scanner un badge',
  'acces.titre.exposantLogin': 'Connexion (espace {lexExhibitorBare})',
  'acces.titre.exposantInscription': 'Inscription {lexExhibitorOf}',
  'acces.titre.exposantDemandeCode': 'Demande de code {lexExhibitorOf}',
  'acces.titre.exposantMaFiche': 'Ma fiche {lexExhibitorBare}',
  'acces.titre.exposantMonStand': 'Mon stand',
  'acces.titre.exposantMesAteliers': 'Mes {lexWorkshopPlural}',
  'acces.titre.exposantMesRencontres': 'Mes rencontres',
  'acces.titre.exposantCvRecus': 'CV reçus',
  'acces.titre.exposantScanner': 'Scanner (espace {lexExhibitorBare})',
  'acces.titre.animateur': 'Espace animation',
  'acces.titre.animateurInscription': 'Inscription (animation)',
  'acces.titre.animateurDemandeCode': 'Demande de code (animation)',
  'acces.titre.animateurMesAteliers': '{lexWorkshopPluralCap} que j’anime',
  'acces.titre.adminLogin': 'Connexion à l’administration',
  'acces.titre.admin2fa': 'Double authentification',
  'acces.titre.adminResetDemande': 'Réinitialisation du mot de passe (demande)',
  'acces.titre.adminReset': 'Réinitialisation du mot de passe',
  'acces.titre.adminMotDePasse': 'Changer mon mot de passe',
  'acces.titre.admin': 'Administration',
  'acces.titre.adminTableauDeBord': 'Tableau de bord en direct',
  'acces.titre.adminMur': 'Mur d’affichage',
  'acces.titre.adminPilotage': 'Pilotage',
  'acces.titre.adminAlertes': 'Alertes',
  'acces.titre.adminActivite': 'Journal d’activité',
  'acces.titre.adminAudit': 'Journal d’audit',
  'acces.titre.adminBilanQuotidien': 'Bilan quotidien',
  'acces.titre.adminErreursInscription': 'Erreurs d’inscription',
  'acces.titre.adminParcours': 'Parcours {lexVisitorOfPlural}',
  'acces.titre.adminAnalyticsEmploi': 'Statistiques emploi',
  'acces.titre.adminAffluence': 'Carte d’affluence',
  'acces.titre.adminStatsFormulaire': 'Statistiques du formulaire',
  'acces.titre.adminFormBuilder': 'Constructeur de formulaire',
  'acces.titre.adminInscriptions': 'Inscriptions',
  'acces.titre.adminFusionVisiteurs': 'Fusion de fiches {lexVisitorOfPluralBare}',
  'acces.titre.adminBadges': 'Badges {lexVisitorOfPlural}',
  'acces.titre.adminExportPresence': 'Export des présences',
  'acces.titre.adminPresenceAteliers': 'Présences {lexWorkshopOfPlural}',
  'acces.titre.adminAnimateurs': 'Animateurs',
  'acces.titre.adminAteliers': '{lexWorkshopPluralCap}',
  'acces.titre.adminProgrammes': 'Programmes',
  'acces.titre.adminProgrammesParRole': 'Programmes par profil',
  'acces.titre.adminImportExposants': 'Import {lexExhibitorOfPluralBare}',
  'acces.titre.adminDemandesCodes': 'Demandes de codes',
  'acces.titre.adminCodesExposants': 'Codes {lexExhibitorOfPluralBare}',
  'acces.titre.adminBilansExposants': 'Bilans {lexExhibitorOfPluralBare}',
  'acces.titre.adminExposants': '{lexExhibitorPluralCap} (administration)',
  'acces.titre.adminStands': 'Stands',
  'acces.titre.adminPlanEditor': 'Editeur de plan',
  'acces.titre.adminPersonne': 'Fiche complète',
  'acces.titre.adminOffres': "Offres d'emploi",
  'settingsAdmin.section.minors': 'Comptes mineurs',
  // Rattachement d une inscription existante a un stand (2026-09-09).
  'standsAdmin.field.linkProfile': 'Rattacher une inscription existante',
  'standsAdmin.field.linkProfile.none': 'Aucune, je saisis le stand à la main',
  'standsAdmin.field.linkProfile.exhibitors': 'Entreprises inscrites',
  'standsAdmin.field.linkProfile.hosts': 'Animateurs inscrits',
  'standsAdmin.field.linkProfile.loading': 'Lecture des inscriptions...',
  'standsAdmin.field.linkProfile.empty': 'Aucune inscription sans stand pour le moment.',
  'standsAdmin.field.linkProfile.help':
    'Seules les inscriptions qui n’ont pas encore de stand sont proposées. Le libellé se remplit tout seul.',
  'standsAdmin.toast.createdNotLinked':
    'Stand créé, mais l’inscription choisie vient d’être rattachée à un autre stand. Rattachez-la depuis sa fiche.',
  'acces.titre.adminPositionsStands': 'Emplacements des stands',
  'acces.titre.adminVisibilite': 'Pièces de visibilité',
  'acces.titre.adminGroupes': 'Groupes',
  'acces.titre.adminListesNominatives': 'Listes nominatives',
  'acces.titre.adminTransport': 'Transport',
  'acces.titre.adminTransportRotation': 'Transport (feuille de route)',
  'acces.titre.adminStatsControle': 'Statistiques de contrôle',
  'acces.titre.adminStatsControleType': 'Statistiques de contrôle (détail)',
  'acces.titre.adminTypesControle': 'Types de contrôle',
  'acces.titre.adminSondages': 'Sondages (administration)',
  'acces.titre.adminMedias': 'Médias (administration)',
  'acces.titre.adminFluxMedias': 'Flux médias',
  'acces.titre.adminPages': 'Pages',
  'acces.titre.adminEditionPage': 'Édition d’une page',
  'acces.titre.adminVersionsPage': 'Versions d’une page',
  'acces.titre.adminContenus': 'Blocs de contenu',
  'acces.titre.adminTuilesAccueil': 'Tuiles de l’accueil',
  'acces.titre.adminNavigation': 'Navigation',
  'acces.titre.adminPartenaires': 'Partenaires (administration)',
  'acces.titre.adminOrganisateurs': 'Organisateurs',
  'acces.titre.adminTags': 'Étiquettes',
  'acces.titre.adminTraductions': 'Traductions',
  'acces.titre.adminModelesEmail': 'Modèles d’e-mails',
  'acces.titre.adminComposeurEmail': 'Envois de courriels',
  'acces.titre.adminEmail2fa': 'Double authentification par e-mail',
  'acces.titre.adminApp2fa': 'Double authentification par application',
  'acces.titre.adminSecuriteComptes': 'Sécurité des comptes',
  'acces.titre.adminSecurite': 'Ma sécurité',
  'acces.titre.adminModelesEmailVip': 'Modèles d’e-mails VIP',
  'acces.titre.adminInvitationsVip': 'Invitations VIP',
  'acces.titre.adminInvitations': 'Invitations',
  'acces.titre.adminPush': 'Notifications push',
  'acces.titre.adminComptes': 'Comptes',
  'acces.titre.adminComptesAdmin': 'Comptes d’administration',
  'acces.titre.adminRoles': 'Rôles',
  'acces.titre.adminAcces': 'Droits d’accès',
  'acces.titre.adminFonctions': 'Fonctions activables',
  'acces.titre.adminConfigEvenement': 'Configuration de l’événement',
  'acces.titre.adminReglages': 'Réglages',
  'acces.titre.adminMaintenance': 'Maintenance',
  'acces.titre.adminJetonsDeploiement': 'Jetons de déploiement',
  'acces.titre.adminWebhooks': 'Webhooks',
  'acces.titre.adminBornes': 'Bornes',
  'acces.titre.adminOsnum': 'État des services',
  'acces.titre.adminIa': 'Configuration de l’assistance IA',
  'acces.titre.introuvable': 'Page introuvable',

  // ── Étiquettes de champs qui n’en avaient aucune (WCAG 4.1.2 / RGAA 11.1) ─
  'acces.mergeVisitors.search.label': 'Rechercher {lexVisitorIndef} par nom, prénom ou adresse e-mail',
  'acces.visitorBadges.search.label': 'Rechercher {lexVisitorIndef} dont afficher le badge',
  'acces.mesRencontres.search.label': 'Rechercher parmi les personnes rencontrées',
  'acces.emailComposer.test.label': 'Adresse e-mail de destination de l’envoi de test',
  'acces.exhibitorsImport.file.label': 'Fichier CSV {lexExhibitorOfPluralBare} à importer',
};
